// fetch cms content service

import {
  ICmsApiRequest,
  ICmsApiResponse,
  IContentData,
  ILabelData,
} from '../../models/interfaces/ICmsContent';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CMSContentStoreService } from '../cms-content-store/cms-content-store.service';
import { HttpClient } from '@angular/common/http';
import { IKeyValuePair } from '../../models/interfaces/IObject';
import { LOCALE_ID } from '../../constants/global.constant';
import { LocaleService } from '../locale-wrapper/locale.service';

@Injectable({
  providedIn: 'root',
})
export class FetchCMSContentService implements OnDestroy {
  ngUnsubscribe = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly _cmsContentStoreService: CMSContentStoreService,
    private readonly localeService: LocaleService,
  ) {}

  /**
   * Get CMS content data and process it
   * @param url endpoint url
   * @param keys cms keys
   * @param locale locale language
   * @returns observable
   */
  getCMSContent(
    url: any,
    keys = [''],
    locale = LOCALE_ID.id,
  ): Observable<ICmsApiResponse | null> {
    keys = keys.filter(
      (key: string) =>
        !this._cmsContentStoreService.contentAppIds.includes(key),
    );
    if (keys.length === 0) {
      return of(null);
    }
    const requestObj = this.createEcmsPayload(keys, locale);
    return this.fetchCMSData(url.GetCMSContentApi.URI, requestObj).pipe(
      takeUntil(this.ngUnsubscribe),
      switchMap((res) => {
        // Check if response is empty or has no data
        const hasLabelData = res?.labelData && res.labelData.length > 0;
        const hasContentData = res?.contentData && res.contentData.length > 0;

        if (!res || (!hasLabelData && !hasContentData)) {
          return this.getCMSJSONContent(url.GetCMSContent.URI);
        }
        this.setMergedLabelsInStore(res);
        return of(res);
      }),
      catchError(() => {
        return this.getCMSJSONContent(url.GetCMSContent.URI);
      }),
    );
  }

  /**
   * Get CMS data and process it
   * @param url endpoint
   * @returns observable
   */
  getCMSJSONContent(url: any): Observable<ICmsApiResponse> {
    return this.getCMSJSONData(url).pipe(
      takeUntil(this.ngUnsubscribe),
      tap((res) => {
        this.setMergedLabelsInStore(res);
      }),
    );
  }

  /**
   * setMergedLabelsInStore
   * @param res labels
   */
  setMergedLabelsInStore(res: ICmsApiResponse) {
    const mergedLabels = this.modifyCMSData(res);
    if (mergedLabels) {
      this._cmsContentStoreService.setLabelsInStore(mergedLabels);
    }
  }

  /**
   * createEcmsPayload
   * @param keys : Array of mapping ids to be fetched from cms
   * @param locale locale language
   * @returns ICmsApiRequest
   */
  createEcmsPayload(
    keys: Array<string>,
    locale = LOCALE_ID.id,
  ): ICmsApiRequest {
    const contentRequestObj = {} as ICmsApiRequest;
    // below values will be const for all apps
    contentRequestObj.locale = this.localeService.getUrlOrSessionLocale();
    contentRequestObj.id = locale; // to be derived from constant value
    this._cmsContentStoreService.contentAppIds.push(...keys);
    contentRequestObj.key = keys;
    return contentRequestObj;
  }

  /**
   * Modify API data
   * @param res : HTTP response
   * @returns Observable
   */
  modifyCMSData(res: ICmsApiResponse): IKeyValuePair[] {
    const labelDataArr: IKeyValuePair[] = [];
    res.labelData?.forEach((obj: ILabelData) => {
      obj.value.forEach((_pair: IKeyValuePair) => {
        labelDataArr.push({
          ..._pair,
          value: this.correctAssetPaths(_pair.value),
        });
      });
    });
    res.contentData?.forEach((obj: IContentData) => {
      labelDataArr.push({
        key: obj.applicationId,
        value: this.correctAssetPaths(
          obj.value && obj.value.bodyHtml ? obj.value.bodyHtml : obj.value,
        ),
      });
    });
    return labelDataArr;
  }

  /**
   * Strips the erroneous '/digitalcollaboration' segment the CMS sometimes
   * prepends to asset URLs (e.g. img src values). That segment is not a valid
   * route in this app (assets are served from '/assets/...'), so leaving it
   * in place results in broken images/links on pages rendering CMS HTML.
   * @param value CMS-provided label text or HTML fragment
   * @returns value with any erroneous asset path prefix removed
   */
  private correctAssetPaths(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }
    return value.replace(
      /((?:src|href)=["'])\/digitalcollaboration\/assets\//g,
      '$1/assets/',
    );
  }

  /**
   * Fetch CMS data from service
   * @param url endpoint
   * @param requestObj request
   * @returns observable
   */
  fetchCMSData(
    url: string,
    requestObj: ICmsApiRequest = {} as ICmsApiRequest,
  ): Observable<ICmsApiResponse> {
    return this.http.post<ICmsApiResponse>(url, requestObj);
  }

  /**
   * Fetch CMS json data from service
   * @param url endpoint
   * @returns observable
   */
  getCMSJSONData(url: string): Observable<ICmsApiResponse> {
    return this.http.get<ICmsApiResponse>(url, {
      headers: {
        isContentJSON: 'true',
      },
    });
  }

  /**
   * lifecycle hook
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
